import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Subscription, SubscriptionMapper } from '@processpuzzle/platform-admin';
import { RUNTIME_CONFIGURATION, serviceRootOf } from '@processpuzzle/util';
import { map, Observable } from 'rxjs';
import { CustomerAccount, CustomerApplication } from './customer-account';
import { CustomerAccountMapper } from './customer-account.mapper';
import { BillingParty } from './signup';

/**
 * The customer portal's four calls, against the gateway's `/customers/**` paths.
 *
 * **Not** platform-admin's `OrganizationBillingService`, `SubscriptionService` or `InvoiceService`.
 * All three point at `PLATFORM_ADMIN_SERVICE_ROOT` and `platform/**` staff paths — a host the Biz
 * frontend cannot reach, behind authority no customer holds. Reusing them would have made the
 * customer portal a caller of the staff surface, which is precisely the widening the gateway exists
 * to avoid. The models and their mappers *are* reused, because those are the same contract shapes;
 * it is the URLs that differ, and the URLs are the security boundary.
 *
 * Every call but one names one organization in its path. The exception, {@link listApplications},
 * takes no parameter at all: the gateway reads the keys from the caller's own token. There is no
 * `where` parameter anywhere, which is what keeps the portal unable to enumerate.
 *
 * All four answer 404 when `platform-subscription.portal.enabled` is false, because the controller
 * behind them is not registered at all. That is indistinguishable from an unknown organization on
 * purpose — telling them apart would publish whether the flag is on — so the portal's error handling
 * treats both as "we could not find that organization".
 */
@Injectable({ providedIn: 'root' })
export class CustomerAccountService {
  private readonly httpClient = inject(HttpClient);
  private readonly mapper = inject(CustomerAccountMapper);
  private readonly subscriptionMapper = inject(SubscriptionMapper);
  private readonly baseUrl = serviceRootOf(inject(RUNTIME_CONFIGURATION), 'SUBSCRIPTION_SERVICE_ROOT');

  /** Every application of the signed-in account; empty for a customer who is a member of nothing yet. */
  listApplications(): Observable<CustomerApplication[]> {
    return this.httpClient
      .get<unknown[]>(`${this.baseUrl}/customers`)
      .pipe(map((dtos) => (Array.isArray(dtos) ? dtos : []).map((dto) => this.mapper.fromApplicationDto(dto))));
  }

  findByOrgKey(orgKey: string): Observable<CustomerAccount> {
    return this.httpClient.get<unknown>(`${this.baseUrl}/customers/${orgKey}`).pipe(map((dto) => this.mapper.fromDto(dto)));
  }

  /**
   * A full replacement, not a patch — every field of a § 14 UStG invoice header is only correct
   * together, and a partial update could leave one company's legal name against another's address.
   *
   * A changed `vatId` clears its stored VIES confirmation on the server. The form warns before
   * saving one for that reason; the rule itself is the server's, because it is about what makes an
   * invoice compliant rather than about what is safe to send.
   */
  updateBillingParty(orgKey: string, billingParty: BillingParty): Observable<BillingParty> {
    return this.httpClient.put<unknown>(`${this.baseUrl}/customers/${orgKey}/billing-party`, billingParty).pipe(map((dto) => this.mapper.toBillingParty(dto)));
  }

  /**
   * Cancels at the end of the paid period. The subscription comes back **still running**: only
   * `cancelAtPeriodEnd` changes, and the status stays as it was, because the customer has paid for
   * the running period and is owed it.
   *
   * POST and DELETE on one cancellation *resource* rather than two verbs, matching the contract:
   * a cancellation is a thing with a state that can be removed, which is what makes resuming
   * impossible to get out of step with cancelling.
   */
  cancelSubscription(orgKey: string): Observable<Subscription> {
    return this.httpClient.post<unknown>(`${this.baseUrl}/customers/${orgKey}/subscription/cancellation`, {}).pipe(map((dto) => this.subscriptionMapper.fromDto(dto)));
  }

  resumeSubscription(orgKey: string): Observable<Subscription> {
    return this.httpClient.delete<unknown>(`${this.baseUrl}/customers/${orgKey}/subscription/cancellation`).pipe(map((dto) => this.subscriptionMapper.fromDto(dto)));
  }
}
